import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    IconButton,
    Snackbar,
    Fade,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useDispatch, useSelector } from 'react-redux';
import {
    _holderPublicKey,
    _signerPublicKey,
    _presentation,
    _verified,
    _publicKeyEncoding,
    _signerPublicKeyEncoding,
    setHolderPublicKey,
    setSignerPublicKey,
    setPresentation,
    setVerified,
    setPublicKeyEncoding,
    setSignerPublicKeyEncoding
} from '../../../redux/verifySlice';
import { _vp } from '../../../redux/presentSlice';
import { verifiable } from 'sd-vc-lib';
import documentLoader from '../../../utils/document-loader';
import _ from 'lodash';
import { hexConverter } from '../utils/hexConverter';
import { Encoding } from '../types/types';
import { WizardButton } from '../../ui/WizardButton';
import { OutputViewToggle } from '../../ui/ActionBtns';
import FieldInput from '../../ui/FeildInput';
import CustomizeJsonEditor from '../../ui/CustomizeJsonEditor';
import SectionDivider from '../../ui/SectionDivider';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { getSignErrorMessage } from '../utils/getSignErrorMessage';
import JsonViewer from '../../ui/JsonViwer';
import useClipboard from '../../hooks/useClipboard';
import { trackEvent } from '../../../utils/google-analytics';

export default function PresentationVerification() {
    const dispatch = useDispatch();

    const { snackBarState, copyToClipboard, handleClose } = useClipboard();

    const holderPublicKey = useSelector(_holderPublicKey);
    const signerPublicKey = useSelector(_signerPublicKey);
    const presentation = useSelector(_presentation);
    const verified = useSelector(_verified);
    const publicKeyEncoding = useSelector(_publicKeyEncoding);
    const signerPublicKeyEncoding = useSelector(_signerPublicKeyEncoding);
    const vp = useSelector(_vp);

    const [draftPresentation, setDraftPresentation] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [viewImagesOpen, setViewImagesOpen] = useState(false);
    const [imageErrorMsg, setImageErrorMsg] = useState('');

    const canVerify = !!presentation;
    const isVerified = verified === 'Presentation Verified';
    const hasResult = !!verified;

    function checkCredential(mask: any = {}, credentials: any = {}): any {
        if (_.isArray(credentials)) {
            const claims: any[] = [];
            for (let key = 0; key < credentials.length; key++) {
                if (_.isObject(mask?.[key]) && _.isObject(credentials?.[key])) {
                    claims.push(checkCredential(mask[key], credentials[key]));
                    continue;
                }
                if (!mask?.[key]) claims.push(credentials[key]);
            }
            return claims;
        }

        if (_.isObject(credentials)) {
            const claims: any = {};
            for (const key in credentials) {
                if (_.isObject(mask?.[key]) && _.isObject(credentials?.[key])) {
                    claims[key] = checkCredential(mask[key], credentials[key]);
                    continue;
                }
                if (!mask?.[key]) claims[key] = credentials[key];
            }
            return claims;
        }
    }

    function getDisclosedCredentials(pres: any): any[] {
        const result: any[] = [];
        for (const credential of pres['verifiableCredential']) {
            const mask =
                credential['credentialSubject']?.['selectiveDisclosureMetaData']?.['mask'] || {};
            const credentials = credential['credentialSubject'] || {};
            result.push(checkCredential(mask, credentials));
        }
        return result;
    }

    function openImageInTab(dataUrl: string) {
        try {
            const [header, base64Data] = dataUrl.split(',');
            const mimeType = header.match(/:(.*?);/)?.[1] ?? 'image/png';
            const bytes = atob(base64Data);
            const arr = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
            const blob = new Blob([arr], { type: mimeType });
            const url = URL.createObjectURL(blob);
            const img = new Image();
            img.onload = () => {
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            };
            img.onerror = () => {
                URL.revokeObjectURL(url);
                setImageErrorMsg('Image data is corrupted or invalid and cannot be displayed.');
            };
            img.src = url;
        } catch {
            setImageErrorMsg('Image data is corrupted or invalid and cannot be displayed.');
        }
    }

    function extractImages(presentationStr: string): Array<{ name: string; data: string }> {
        try {
            const pres = JSON.parse(presentationStr);
            const images: Array<{ name: string; data: string }> = [];
            for (const credential of pres['verifiableCredential'] || []) {
                const subject = credential['credentialSubject'] || {};
                for (const [key, value] of Object.entries(subject)) {
                    if (typeof value === 'string' && value.startsWith('data:image/')) {
                        images.push({ name: key, data: value });
                    }
                }
            }
            return images;
        } catch {
            return [];
        }
    }

    async function verify() {
        dispatch(setVerified(''));
        try {
            if (!holderPublicKey || !signerPublicKey)
                throw new Error('Holder and Signer public keys are required for verification');

            const vp = JSON.parse(presentation);

            const publicKey = hexConverter(holderPublicKey, publicKeyEncoding as Encoding);
            const issuerPublicKey = hexConverter(
                signerPublicKey,
                signerPublicKeyEncoding as Encoding
            );

            // await verifyProvidedPublicKeys(vp, publicKey, issuerPublicKey);

            const result = await verifiable.presentation.verify({
                vp,
                holderPublicKey: publicKey,
                issuerPublicKey,
                documentLoader
            });

            if (!result?.verified) throw new Error();
            dispatch(setVerified('Presentation Verified'));
            trackEvent('Credential Studio', 'presentation_verified');
        } catch (e) {
            console.error(e);
            console.log(e);
            const message = getSignErrorMessage(e, 'Verify');
            dispatch(setVerified(message));
            trackEvent('Credential Studio', 'presentation_verify_failed', message);
        }
    }

    let disclosedCredentials: any[] | null = null;
    if (isVerified && presentation) {
        try {
            disclosedCredentials = getDisclosedCredentials(JSON.parse(presentation));
        } catch {
            disclosedCredentials = null;
        }
    }

    React.useEffect(() => {
        dispatch(setVerified(''));
        if (!vp) {
            dispatch(setPresentation(''));
            return;
        }
        try {
            const decoded = JSON.stringify(JSON.parse(atob(vp)), null, 2);
            dispatch(setPresentation(decoded));
        } catch {
            dispatch(setPresentation(vp));
        }
    }, [vp]);

    return (
        <div>
            <SectionDivider />
            <Grid container spacing={3} alignItems="stretch">
                {/* LEFT PANEL */}
                <Grid item xs={12} md={7}>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 20, flex: 1 }}>
                        <Box>
                            <Box
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    marginBottom: 12
                                }}>
                                <Typography
                                    style={{
                                        fontWeight: 600,
                                        fontSize: '0.95rem'
                                    }}>
                                    Verifiable Presentation
                                </Typography>
                                {presentation && (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            backgroundColor: '#e8faf7',
                                            color: '#2ec4b6',
                                            border: '1px solid #b2ede8',
                                            borderRadius: 6,
                                            padding: '2px 10px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600
                                        }}>
                                        + FROM STEP 3
                                    </span>
                                )}
                                {!presentation ||
                                    (!isEditing && (
                                        <Box
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                flex: 1
                                            }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => copyToClipboard(presentation)}
                                                style={{
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 6,
                                                    padding: 6,
                                                    backgroundColor: '#fff'
                                                }}>
                                                <FileCopyOutlinedIcon
                                                    style={{ fontSize: 16, color: '#555' }}
                                                />
                                            </IconButton>
                                        </Box>
                                    ))}
                            </Box>
                            <Box>
                                <Box>
                                    {!presentation || isEditing ? (
                                        <CustomizeJsonEditor
                                            value={draftPresentation}
                                            onChange={setDraftPresentation}
                                            onJsonEntered={(json) => {
                                                dispatch(
                                                    setPresentation(JSON.stringify(json, null, 2))
                                                );
                                                dispatch(setVerified(''));
                                                setIsEditing(false);
                                            }}
                                        />
                                    ) : (
                                        <Box>
                                            <JsonViewer content={presentation} />
                                        </Box>
                                    )}
                                    {!isEditing && presentation && (
                                        <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                            style={{ marginTop: 10, gap: 10 }}>
                                            {presentation &&
                                                extractImages(presentation).length > 0 && (
                                                    <WizardButton
                                                        variant="outlined"
                                                        style={{ fontSize: 12 }}
                                                        onClick={() => setViewImagesOpen(true)}>
                                                        Extract Media
                                                    </WizardButton>
                                                )}
                                            <WizardButton
                                                variant="outlined"
                                                style={{ fontSize: 12 }}
                                                onClick={() => {
                                                    setDraftPresentation(presentation);
                                                    setIsEditing(true);
                                                }}>
                                                Edit JSON
                                            </WizardButton>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                {/* RIGHT PANEL */}
                <Grid item xs={12} md={5} style={{ display: 'flex', alignSelf: 'flex-start' }}>
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            width: '100%',
                            gap: 20
                        }}>
                        <Box style={{ marginBottom: 20 }}>
                            <Typography
                                style={{
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    marginBottom: 8
                                }}>
                                Keys Encoding
                            </Typography>
                            <SectionDivider />
                            <Box style={{ marginBottom: 20 }}>
                                <Typography
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 8,
                                        background: '#f5f5f5',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 8,
                                        padding: '9px 12px'
                                    }}>
                                    <InfoOutlined
                                        style={{
                                            fontSize: 14,
                                            color: '#999',
                                            flexShrink: 0,
                                            marginTop: 2
                                        }}
                                    />
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: '#888',
                                            lineHeight: 1.6,
                                            fontStyle: 'italic'
                                        }}>
                                        Add the holder's and signer's public keys to verify the
                                        presentation. Switch between Base58, Base64, and Hex to
                                        match your key format.
                                    </span>
                                </Typography>
                            </Box>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ marginBottom: 6 }}>
                                <Typography style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                                    Holder's Public Key
                                </Typography>
                                <OutputViewToggle
                                    tab={
                                        publicKeyEncoding === 'Base58'
                                            ? 0
                                            : publicKeyEncoding === 'Base64'
                                              ? 1
                                              : 2
                                    }
                                    onTabChange={(idx) =>
                                        dispatch(
                                            setPublicKeyEncoding(
                                                idx === 0 ? 'Base58' : idx === 1 ? 'Base64' : 'Hex'
                                            )
                                        )
                                    }
                                    options={[
                                        { label: 'Base58' },
                                        { label: 'Base64' },
                                        { label: 'Hex' }
                                    ]}
                                />
                            </Box>
                            <FieldInput
                                value={holderPublicKey || ''}
                                onChange={(v) => dispatch(setHolderPublicKey(v))}
                                placeholder="Holder's public key..."
                                multiline
                                rows={3}
                                style={{ width: '100%', border: '1px solid #1eb89a' }}
                            />
                        </Box>
                        <Box>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                style={{ marginBottom: 6 }}>
                                <Typography style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                                    Signer's Public Key
                                </Typography>
                                <OutputViewToggle
                                    tab={
                                        signerPublicKeyEncoding === 'Base58'
                                            ? 0
                                            : signerPublicKeyEncoding === 'Base64'
                                              ? 1
                                              : 2
                                    }
                                    onTabChange={(idx) =>
                                        dispatch(
                                            setSignerPublicKeyEncoding(
                                                idx === 0 ? 'Base58' : idx === 1 ? 'Base64' : 'Hex'
                                            )
                                        )
                                    }
                                    options={[
                                        { label: 'Base58' },
                                        { label: 'Base64' },
                                        { label: 'Hex' }
                                    ]}
                                />
                            </Box>
                            <FieldInput
                                value={signerPublicKey || ''}
                                onChange={(v) => dispatch(setSignerPublicKey(v))}
                                multiline
                                rows={3}
                                placeholder="Signer's public key..."
                                style={{ width: '100%', border: '1px solid #1eb89a' }}
                            />
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <WizardButton
                        variant="primary"
                        fullWidth
                        disabled={!canVerify || isEditing}
                        onClick={verify}
                        endIcon={<VerifiedUserIcon style={{ fontSize: '1.1rem' }} />}>
                        Next: Verify presentation
                    </WizardButton>
                </Grid>

                <Grid item xs={12}>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {hasResult && (
                            <Box
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    padding: 12,
                                    borderRadius: 8,
                                    backgroundColor: isVerified ? '#f0faf7' : '#fff4f4',
                                    border: isVerified ? '1px solid #b7eadc' : '1px solid #ffcccc'
                                }}>
                                <Box style={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography style={{ fontWeight: 400, color: '#666' }}>
                                        {isVerified
                                            ? 'Presentation verified : All checks passed'
                                            : verified}
                                    </Typography>
                                </Box>
                                {isVerified ? (
                                    <CheckCircleIcon style={{ color: '#2e7d32' }} />
                                ) : (
                                    <ErrorOutlineIcon style={{ color: '#d32f2f' }} />
                                )}
                            </Box>
                        )}

                        {isVerified && (
                            <>
                                <Grid item xs={12}>
                                    <JsonViewer
                                        content={presentation}
                                        header="Decoded Presentation"
                                        onCopy={copyToClipboard}
                                    />
                                </Grid>
                            </>
                        )}

                        {isVerified && disclosedCredentials && (
                            <>
                                <Grid item xs={12}>
                                    <JsonViewer
                                        content={disclosedCredentials}
                                        header="Disclosed Credentials"
                                        onCopy={copyToClipboard}
                                        maxHeight={200}
                                    />
                                </Grid>
                            </>
                        )}
                    </Box>
                </Grid>
            </Grid>
            <Snackbar
                open={snackBarState.open}
                onClose={handleClose}
                TransitionComponent={Fade}
                message={snackBarState.text}
                autoHideDuration={5000}
            />
            <Snackbar
                open={!!imageErrorMsg}
                onClose={() => setImageErrorMsg('')}
                TransitionComponent={Fade}
                autoHideDuration={5000}
                message={imageErrorMsg}
                ContentProps={{
                    style: {
                        backgroundColor: '#fff4f4',
                        border: '1px solid #ffccc',
                        fontWeight: 400,
                        color: '#666'
                    }
                }}
            />

            <Dialog
                open={viewImagesOpen}
                onClose={() => setViewImagesOpen(false)}
                maxWidth="sm"
                fullWidth>
                <DialogTitle disableTypography style={{ padding: '16px 24px 8px' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography style={{ fontWeight: 600, fontSize: '1rem' }}>
                            Images
                        </Typography>
                        <IconButton size="small" onClick={() => setViewImagesOpen(false)}>
                            <CloseIcon style={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent style={{ paddingBottom: 24 }}>
                    {(() => {
                        const images = presentation ? extractImages(presentation) : [];
                        return images.length === 0 ? (
                            <Typography style={{ color: '#888', padding: '8px 0' }}>
                                No images found in the presentation.
                            </Typography>
                        ) : (
                            <List disablePadding>
                                {images.map((img, idx) => (
                                    <ListItem
                                        key={idx}
                                        style={{
                                            borderBottom: '1px solid #f0f0f0',
                                            paddingLeft: 0,
                                            paddingRight: 0
                                        }}>
                                        <ListItemText
                                            primary={img.name}
                                            primaryTypographyProps={{
                                                style: { fontWeight: 500, fontSize: '0.9rem' }
                                            }}
                                        />
                                        <WizardButton
                                            variant="outlined"
                                            style={{ fontSize: 12, padding: 2 }}
                                            onClick={() => openImageInTab(img.data)}>
                                            View
                                        </WizardButton>
                                    </ListItem>
                                ))}
                            </List>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
}
